/**
 * «Enviar» (docs/29): manda un mensaje, un enlace o un PDF a terceros DESDE LA CUENTA DEL USUARIO.
 *
 *   - Correo: por el Gmail / Outlook que el usuario conectó en Mensajes (si no hay, se le manda
 *     a conectarlo al panel del hub).
 *   - WhatsApp (asistido): el hub devuelve un `wa.me` por destinatario; aquí se lista «Abrir
 *     WhatsApp para X», uno por clic (un `window.open` tras un `await` lo bloquea el navegador).
 *
 * Reutilizable desde cualquier pantalla: el texto, el enlace y el adjunto llegan por props.
 * La lógica (validar, armar la entrada, la lista de chats) vive en `lib/enviarMensaje.ts`.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Radio,
  RadioGroup,
  Select,
  Spinner,
  Textarea,
} from '@fluentui/react-components';
import { Add16Regular, Attach16Regular, Delete16Regular, Open16Regular } from '@fluentui/react-icons';
import {
  correosConectados,
  enviarMensaje,
  LIMITES_POR_DEFECTO,
  textoDeError,
  type AppOrigen,
  type ProveedorCorreo,
} from '../../api/mensajes.js';
import {
  armarEntrada,
  blobABase64,
  chatsPorAbrir,
  filasParaCanal,
  marcarAbierto,
  pendientes,
  sinErrores,
  textoMB,
  validarAdjunto,
  validarEnvio,
  type CanalDeEnvio,
  type ChatPorAbrir,
  type ErroresEnvio,
  type FilaDestinatario,
} from '../../lib/enviarMensaje.js';
import { abrirPanelMensajes, useMensajes, useMensajesStore } from '../../lib/mensajesDisponibles.js';

export interface AdjuntoParaEnviar {
  nombre: string;
  blob: Blob;
}

export interface EnviarDialogProps {
  abierto: boolean;
  onCerrar: () => void;
  origen: { app: AppOrigen; ref?: string };
  titulo?: string;
  canalInicial?: CanalDeEnvio;
  para?: FilaDestinatario[];
  asunto?: string;
  /** Mensaje del correo (y de WhatsApp si no hay `mensajeWhatsApp`). */
  mensaje?: string;
  /** Texto para WhatsApp (más corto que el del correo). */
  mensajeWhatsApp?: string;
  enlace?: { url: string; titulo: string } | null;
  /** Adjunto ya generado, o cómo generarlo al abrir (ej. bajar el PDF del resumen). */
  cargarAdjunto?: () => Promise<AdjuntoParaEnviar | null>;
  /** Deja elegir un PDF del equipo (por omisión, sí). */
  permitirAdjuntar?: boolean;
}

const NOMBRE_PROVEEDOR: Record<ProveedorCorreo, string> = { gmail: 'Gmail', outlook: 'Outlook' };
const FILA_VACIA: FilaDestinatario = { nombre: '', contacto: '' };

export function EnviarDialog(props: EnviarDialogProps): JSX.Element {
  const { abierto, onCerrar, origen, titulo, permitirAdjuntar = true } = props;
  const { estado } = useMensajes();
  const limites = estado?.limites ?? LIMITES_POR_DEFECTO;
  const correos = useMemo(() => correosConectados(estado), [estado]);

  const [canal, setCanal] = useState<CanalDeEnvio>(props.canalInicial ?? 'correo');
  const [proveedor, setProveedor] = useState<ProveedorCorreo | null>(null);
  const [filas, setFilas] = useState<FilaDestinatario[]>([FILA_VACIA]);
  const [asunto, setAsunto] = useState('');
  const [mensajeCorreo, setMensajeCorreo] = useState('');
  const [mensajeWa, setMensajeWa] = useState('');
  const [adjunto, setAdjunto] = useState<AdjuntoParaEnviar | null>(null);
  const [cargandoAdjunto, setCargandoAdjunto] = useState(false);
  const [errorAdjunto, setErrorAdjunto] = useState<string | null>(null);
  const [errores, setErrores] = useState<ErroresEnvio | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [errorEnvio, setErrorEnvio] = useState<string | null>(null);
  const [enviado, setEnviado] = useState<string | null>(null);
  const [chats, setChats] = useState<ChatPorAbrir[] | null>(null);
  const archivoRef = useRef<HTMLInputElement>(null);

  // Al abrir: se llena con lo que trae la pantalla (queda editable).
  useEffect(() => {
    if (!abierto) return;
    setCanal(props.canalInicial ?? 'correo');
    setFilas(props.para && props.para.length ? props.para.map((p) => ({ ...p })) : [{ ...FILA_VACIA }]);
    setAsunto(props.asunto ?? '');
    setMensajeCorreo(props.mensaje ?? '');
    setMensajeWa(props.mensajeWhatsApp ?? props.mensaje ?? '');
    setAdjunto(null);
    setErrorAdjunto(null);
    setErrores(null);
    setErrorEnvio(null);
    setEnviado(null);
    setChats(null);
    if (props.cargarAdjunto) {
      let vivo = true;
      setCargandoAdjunto(true);
      props
        .cargarAdjunto()
        .then((a) => vivo && setAdjunto(a))
        .catch(() => vivo && setErrorAdjunto('No se pudo preparar el archivo. Puedes enviar el mensaje sin él.'))
        .finally(() => vivo && setCargandoAdjunto(false));
      return () => {
        vivo = false;
      };
    }
    return undefined;
    // Solo al abrir: lo demás lo edita el usuario.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierto]);

  // Proveedor: el primero conectado (Gmail antes que Outlook, en el orden que mande el hub).
  useEffect(() => {
    if (proveedor && correos.some((c) => c.tipo === proveedor)) return;
    setProveedor((correos[0]?.tipo as ProveedorCorreo | undefined) ?? null);
  }, [correos, proveedor]);

  const mensaje = canal === 'correo' ? mensajeCorreo : mensajeWa;
  const setMensaje = canal === 'correo' ? setMensajeCorreo : setMensajeWa;
  const errorDelAdjunto = validarAdjunto(adjunto ? adjunto.blob.size : null, limites);
  const sinCorreo = canal === 'correo' && correos.length === 0;
  const restantes = Math.max(0, limites.por_dia - limites.usados_hoy);

  const cambiarFila = (i: number, campo: keyof FilaDestinatario, valor: string) =>
    setFilas((fs) => fs.map((f, j) => (j === i ? { ...f, [campo]: valor } : f)));

  const elegirArchivo = (file: File | undefined) => {
    if (!file) return;
    if (file.type && file.type !== 'application/pdf') {
      setErrorAdjunto('Solo se pueden adjuntar archivos PDF.');
      return;
    }
    setErrorAdjunto(null);
    setAdjunto({ nombre: file.name, blob: file });
  };

  async function enviar() {
    const form = {
      canal,
      proveedor: canal === 'correo' ? proveedor : null,
      para: filas,
      asunto,
      mensaje,
      enlace: props.enlace ?? null,
      adjuntoBytes: adjunto ? adjunto.blob.size : null,
    };
    const e = validarEnvio(form, limites);
    setErrores(e);
    if (!sinErrores(e)) return;
    setEnviando(true);
    setErrorEnvio(null);
    try {
      const adj = adjunto
        ? { nombre: adjunto.nombre, tipo: adjunto.blob.type || 'application/pdf', base64: await blobABase64(adjunto.blob) }
        : null;
      const entrada = armarEntrada(form, origen, adj);
      const r = await enviarMensaje(entrada);
      if (r.abrir) {
        setChats(chatsPorAbrir(r.abrir, entrada.para));
      } else {
        const n = entrada.para.length;
        setEnviado(
          r.estado === 'en_cola'
            ? 'Tu mensaje quedó en la fila de envío: saldrá en unos minutos desde tu correo.'
            : `Listo: se envió desde tu correo a ${n === 1 ? '1 destinatario' : `${n} destinatarios`}.`,
        );
      }
      // Los «usados hoy» cambiaron: se vuelve a pedir el estado.
      void useMensajesStore.getState().refrescar();
    } catch (err) {
      setErrorEnvio(textoDeError(err));
    } finally {
      setEnviando(false);
    }
  }

  const terminado = enviado !== null || chats !== null;

  return (
    <Dialog open={abierto} onOpenChange={(_, d) => !d.open && onCerrar()}>
      <DialogSurface style={{ maxWidth: 640 }}>
        <DialogBody>
          <DialogTitle>{titulo ?? 'Enviar'}</DialogTitle>
          <DialogContent>
            {terminado ? (
              <div style={{ display: 'grid', rowGap: 12 }}>
                {enviado && (
                  <MessageBar intent="success">
                    <MessageBarBody>{enviado}</MessageBarBody>
                  </MessageBar>
                )}
                {chats && (
                  <>
                    <p style={{ margin: 0 }}>
                      {chats.length === 0
                        ? 'No hubo chats por abrir.'
                        : 'Se abrirá WhatsApp con el mensaje ya escrito: tú solo pulsas enviar. Abre un chat a la vez.'}
                    </p>
                    <div style={{ display: 'grid', rowGap: 8 }}>
                      {chats.map((c, i) => (
                        <Button
                          key={c.url + i}
                          as="a"
                          href={c.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          appearance={c.abierto ? 'secondary' : 'primary'}
                          icon={<Open16Regular />}
                          onClick={() => setChats((l) => (l ? marcarAbierto(l, i) : l))}
                        >
                          {c.abierto ? `Abierto: ${c.etiqueta}` : `Abrir WhatsApp para ${c.etiqueta}`}
                        </Button>
                      ))}
                    </div>
                    {chats.length > 1 && (
                      <p style={{ margin: 0, fontSize: 12, color: 'var(--text-tertiary)' }}>
                        {pendientes(chats) === 0 ? 'Ya abriste todos.' : `Faltan ${pendientes(chats)} por abrir.`}
                      </p>
                    )}
                  </>
                )}
              </div>
            ) : (
              <div style={{ display: 'grid', rowGap: 12 }}>
                <Field label="Enviar por">
                  <RadioGroup layout="horizontal" value={canal} onChange={(_, d) => {
                      const c = d.value as CanalDeEnvio;
                      setCanal(c);
                      setFilas((fs) => filasParaCanal(fs, c));
                      setErrores(null);
                    }}>
                    <Radio value="correo" label="Correo" />
                    <Radio value="whatsapp" label="WhatsApp" />
                  </RadioGroup>
                </Field>

                {canal === 'correo' &&
                  (sinCorreo ? (
                    <MessageBar intent="warning">
                      <MessageBarBody>
                        Conecta tu correo en Mensajes para enviar desde tu propia cuenta.{' '}
                        <Button
                          size="small"
                          appearance="primary"
                          onClick={() => abrirPanelMensajes('canales', { nuevaPestana: true })}
                        >
                          Conectar mi correo
                        </Button>
                      </MessageBarBody>
                    </MessageBar>
                  ) : correos.length > 1 ? (
                    <Field label="Desde">
                      <Select value={proveedor ?? ''} onChange={(_, d) => setProveedor(d.value as ProveedorCorreo)}>
                        {correos.map((c) => (
                          <option key={c.tipo} value={c.tipo}>
                            {NOMBRE_PROVEEDOR[c.tipo as ProveedorCorreo]}
                            {c.cuenta ? ` · ${c.cuenta}` : ''}
                          </option>
                        ))}
                      </Select>
                    </Field>
                  ) : (
                    <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>
                      Desde: <b>{NOMBRE_PROVEEDOR[correos[0]!.tipo as ProveedorCorreo]}</b>
                      {correos[0]!.cuenta ? ` · ${correos[0]!.cuenta}` : ''}
                    </p>
                  ))}

                <Field
                  label={`Para (hasta ${limites.destinatarios_max})`}
                  validationState={errores?.general.some((g) => g.includes('destinatario')) ? 'error' : 'none'}
                >
                  <div style={{ display: 'grid', rowGap: 6 }}>
                    {filas.map((f, i) => (
                      <div key={i}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1.3fr) auto', gap: 6 }}>
                          <Input
                            aria-label={`Nombre del destinatario ${i + 1}`}
                            placeholder="Nombre"
                            value={f.nombre}
                            maxLength={120}
                            onChange={(_, d) => cambiarFila(i, 'nombre', d.value)}
                          />
                          <Input
                            aria-label={canal === 'correo' ? `Correo del destinatario ${i + 1}` : `WhatsApp del destinatario ${i + 1}`}
                            type={canal === 'correo' ? 'email' : 'tel'}
                            placeholder={canal === 'correo' ? 'cliente@empresa.com' : '52 33 1234 5678'}
                            value={f.contacto}
                            onChange={(_, d) => cambiarFila(i, 'contacto', d.value)}
                          />
                          <Button
                            appearance="subtle"
                            icon={<Delete16Regular />}
                            aria-label="Quitar destinatario"
                            disabled={filas.length === 1}
                            onClick={() => setFilas((fs) => fs.filter((_, j) => j !== i))}
                          />
                        </div>
                        {errores?.filas[i] && (
                          <div style={{ fontSize: 12, color: 'var(--bad)', marginTop: 2 }}>{errores.filas[i]}</div>
                        )}
                      </div>
                    ))}
                    <div>
                      <Button
                        size="small"
                        appearance="subtle"
                        icon={<Add16Regular />}
                        disabled={filas.length >= limites.destinatarios_max}
                        onClick={() => setFilas((fs) => [...fs, { ...FILA_VACIA }])}
                      >
                        Agregar destinatario
                      </Button>
                    </div>
                  </div>
                </Field>

                {canal === 'correo' && (
                  <Field label="Asunto">
                    <Input value={asunto} maxLength={200} onChange={(_, d) => setAsunto(d.value)} />
                  </Field>
                )}
                <Field label="Mensaje">
                  <Textarea rows={canal === 'correo' ? 8 : 5} value={mensaje} onChange={(_, d) => setMensaje(d.value)} />
                </Field>

                {props.enlace && !mensaje.includes(props.enlace.url) && (
                  <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>
                    Se incluye el enlace: <b>{props.enlace.titulo}</b>
                  </p>
                )}

                {(permitirAdjuntar || adjunto || cargandoAdjunto) && (
                  <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                    {cargandoAdjunto ? (
                      <Spinner size="tiny" label="Preparando el archivo…" />
                    ) : adjunto ? (
                      <>
                        <Attach16Regular />
                        <span style={{ fontSize: 13 }}>
                          {adjunto.nombre} · {textoMB(adjunto.blob.size)}
                        </span>
                        <Button size="small" appearance="subtle" onClick={() => setAdjunto(null)}>
                          Quitar
                        </Button>
                      </>
                    ) : (
                      permitirAdjuntar && (
                        <>
                          <Button size="small" icon={<Attach16Regular />} onClick={() => archivoRef.current?.click()}>
                            Adjuntar PDF (opcional)
                          </Button>
                          <input
                            ref={archivoRef}
                            type="file"
                            accept="application/pdf,.pdf"
                            hidden
                            onChange={(e) => {
                              elegirArchivo(e.target.files?.[0]);
                              e.target.value = '';
                            }}
                          />
                        </>
                      )
                    )}
                    <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                      Máximo {limites.adjunto_max_mb} MB.{canal === 'whatsapp' ? ' En WhatsApp el PDF va como enlace.' : ''}
                    </span>
                  </div>
                )}
                {(errorAdjunto || errorDelAdjunto) && (
                  <div style={{ fontSize: 12, color: 'var(--bad)' }}>{errorAdjunto ?? errorDelAdjunto}</div>
                )}

                <p style={{ margin: 0, fontSize: 12, color: 'var(--text-tertiary)' }}>
                  Te quedan {restantes} de {limites.por_dia} envíos hoy.
                </p>

                {errores && errores.general.length > 0 && (
                  <MessageBar intent="error">
                    <MessageBarBody>
                      {errores.general.map((g) => (
                        <div key={g}>{g}</div>
                      ))}
                    </MessageBarBody>
                  </MessageBar>
                )}
                {errorEnvio && (
                  <MessageBar intent="error">
                    <MessageBarBody>{errorEnvio}</MessageBarBody>
                  </MessageBar>
                )}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onCerrar}>
              {terminado ? 'Cerrar' : 'Cancelar'}
            </Button>
            {!terminado && (
              <Button
                appearance="primary"
                disabled={enviando || cargandoAdjunto || sinCorreo}
                onClick={() => void enviar()}
              >
                {enviando ? 'Enviando…' : canal === 'correo' ? 'Enviar desde mi correo' : 'Preparar WhatsApp'}
              </Button>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

